'use client'

import { InterfaceChainUtils } from '@morpho-org/dapp-core'
import { useRouter } from 'next/navigation'
import { useCallback } from 'react'

import { useWeb3Context } from '@/contexts/Web3Context/Web3.context'
import { getRouteType } from '@/helpers/routeType'

/**
 * Hook for route-aware navigation.
 * Navigating to a chain-specific path also sets the target chain from its first segment.
 * @returns A function to navigate to a path
 */
export const useNetworkNavigation = () => {
  const { onSetTargetChain } = useWeb3Context()
  const router = useRouter()

  const navigateTo = useCallback(
    (pathName: string, onStartNavigation?: () => void) => {
      const routeType = getRouteType(pathName)

      if (routeType === 'multi-chain') {
        // Navigating to a multi-chain page — no chain extraction needed
        router.push(pathName)
        onStartNavigation?.()
        return
      }

      // Chain-specific target: extract chain from path
      const chainName = pathName.split('/')[1]
      const chain = InterfaceChainUtils.tryGetChain(chainName)

      if (!chain) {
        router.push(pathName)
        onStartNavigation?.()
        return
      }

      onSetTargetChain(chain)
      router.push(pathName)
      onStartNavigation?.()
    },
    [onSetTargetChain, router]
  )

  return { navigateTo }
}
